import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { logger } from '@/utils/logger';
import { captureError, pushBreadcrumb } from '@/debug/debugStore';
import { mapProfileToFormData } from '@/utils/mapTutorProfileToFormData';
import type { ProfileLike } from '@/utils/mapTutorProfileToFormData';
import Modal from '../Modal/Modal';
import MultiSelect from '../common/MultiSelect';
import SearchableDropdown from '../common/SearchableDropdown';
import SearchableInput from '../common/SearchableInput';
import TuitionTypeOptionContent from '../common/TuitionTypeOptionContent';
import WeeklyTimeTable from '../common/WeeklyTimeTable';
import TabButtonGroup from '../common/TabButtonGroup';
import Card from '../common/Card';
import SingleRangeSlider from '../common/SingleRangeSlider';
import TuitionFeeReferenceModal from '../Modal/TuitionFeeReferenceModal';
import PastPublicExamResults from '../common/PastPublicExamResults';
import LevelMultiSelect from './LevelMultiSelect';
import {
  UNIVERSITIES,
  MAJOR_OPTIONS,
  TEACHING_LANGUAGES,
  CURRENT_STATUS_OPTIONS,
  EDUCATION_LEVELS_HIGHEST,
  TUTOR_EXPERIENCE_TYPES,
  hasTeacherRelatedExperience,
  TUITION_EXPERIENCE_YEARS,
  TEACHING_STYLE_OPTIONS,
} from '../../constants/dropdownOptions';
import { Plus, Trash2, AlertCircle, Check, ArrowRight, X } from 'lucide-react';
import type { ExamResult, TutorRegistrationFormData, MultiLevelSubjectCard } from '../../types';
import type { UpdateTutorProfileRequest, SubjectCard } from '../../types/api';
import { stepSchemas, tutorRegistrationSchema } from '../../schemas/tutorRegistrationSchemas';
import { updateTutorProfile, type TutorCompletionMissingField } from '../../api/services/profilesApi';
import { useAuthContext } from '../../contexts/useAuthContext';
import { useModal } from '../../contexts/useModal';
import type { TutorRegistrationModalOptions } from '../../contexts/ModalContext';
import { useTutorCompletionStatus } from '../../hooks/api/useTutorCompletionStatus';
import { useOptionalI18n } from '@/i18n';
import { useLocalizedPath } from '@/i18n/navigation';
import { localizeOptions, localizeStringOptions, localizeText } from '@/i18n/options';
import { getWeeklyTimeTableI18nProps } from '@/utils/weeklyTimeTableI18n';
import {
  savePendingRegistration,
  getPendingRegistration,
  clearPendingRegistration,
} from '../../utils/storage';
import {
  EDUCATION_LEVEL_LABELS,
  EDUCATION_SYSTEM_OPTIONS,
  getSubjectsForLevels,
  type EducationLevel,
  STATE_OPTIONS,
  STATE_DISTRICTS,
} from '@tutor/types';

interface TutorRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  options?: TutorRegistrationModalOptions;
}

const steps = [
  'Tuition Type',
  'Teaching Preferences',
  'Subjects by Level',
  'Personal Profile',
  'Hourly Rate & Languages',
  'Tutor Experience & Exam',
  'Teaching Style & About Me',
];

const createInitialTutorRegistrationFormData = (): TutorRegistrationFormData => ({
  loginMethod: '',
  fullName: '',
  phone: '',
  dateOfBirth: '',
  gender: '',
  nationality: '',
  occupation: '',
  tuitionType: '',
  preferredState: '',
  preferredDistricts: [],
  preferredAreaNotes: [],
  willingToTravel: false,
  maxTravelDistance: '',
  subjectCards: [
    {
      id: Date.now().toString(),
      educationSystem: '',
      educationLevels: [],
      subjects: [],
    },
  ],
  currentStatus: '',
  school: '',
  teachingInstitution: '',
  major: '',
  highestEducationLevel: '',
  otherEducationLevel: '',
  availability: {},
  availabilityTimeRanges: {},
  teachingLanguages: [],
  customLanguage: '',
  hourlyRate: '50',
  tutorExperienceTypes: [],
  yearsOfTuitionExperience: '',
  examResults: [],
  teachingStyle: {
    iAm: [],
    iCan: [],
    iKnow: [],
    others: '',
  },
  aboutMe: '',
  agreedToTerms: false,
});

const deriveWillingToTravel = (tuitionType: TutorRegistrationFormData['tuitionType']) =>
  tuitionType === 'physical' || tuitionType === 'both';

// Helper function to extract field errors from ZodError
const AreaNoteInput: React.FC<{ onAdd: (note: string) => void }> = ({ onAdd }) => {
  const { locale } = useOptionalI18n();
  const [input, setInput] = useState('');
  const commit = () => {
    const trimmed = input.trim();
    if (trimmed) { onAdd(trimmed); setInput(''); }
  };
  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        maxLength={80}
        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
        placeholder={localizeText(locale, 'e.g. Taman Desa, Bangsar Baru, Sri Petaling...')}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
      />
      <button
        type="button"
        onClick={commit}
        className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
      >
        {localizeText(locale, 'Add')}
      </button>
    </div>
  );
};

const extractFieldErrors = (zodError: z.ZodError): Record<string, string> => {
  const fieldErrors: Record<string, string> = {};

  zodError.issues.forEach((issue) => {
    const fieldPath = issue.path.join('.');
    if (fieldPath && !fieldErrors[fieldPath]) {
      fieldErrors[fieldPath] = issue.message;
    }
  });

  return fieldErrors;
};

const COMPLETE_PROFILE_FIELD_LABELS: Record<string, string> = {
  tuitionType: 'Tuition type',
  preferredState: 'State',
  preferredDistricts: 'Preferred districts',
  availability: 'Teaching availability',
  subjectCards: 'Subjects and levels',
  currentStatus: 'Current status',
  highestEducationLevel: 'Highest education level',
  otherEducationLevel: 'Education level details',
  school: 'School or university',
  major: 'Major of study',
  teachingLanguages: 'Teaching languages',
  customLanguage: 'Custom teaching language',
  hourlyRate: 'Hourly rate',
  tutorExperienceTypes: 'Tutoring experience',
  yearsOfTuitionExperience: 'Years of tuition experience',
  examResults: 'Public exam results',
  aboutMe: 'About Me',
  agreedToTerms: 'Terms and conditions',
};

const getFieldAnchor = (fieldName: string): string => {
  if (fieldName.startsWith('subjectCards')) return 'subjectCards';
  if (fieldName.startsWith('teachingLanguages')) return 'teachingLanguages';
  if (fieldName.startsWith('tutorExperienceTypes')) return 'tutorExperienceTypes';
  if (fieldName.startsWith('examResults')) return 'examResults';
  if (fieldName.startsWith('preferredDistricts')) return 'preferredDistricts';
  if (fieldName.startsWith('preferredState')) return 'preferredState';
  return fieldName;
};

const getFieldLabel = (fieldName: string): string =>
  COMPLETE_PROFILE_FIELD_LABELS[getFieldAnchor(fieldName)] || COMPLETE_PROFILE_FIELD_LABELS[fieldName] || fieldName;

const MISSING_FIELD_KEY_TO_FIELD: Record<string, { step: number; fieldName: string }> = {
  tuition_type: { step: 0, fieldName: 'tuitionType' },
  teaching_area: { step: 1, fieldName: 'preferredState' },
  availability: { step: 1, fieldName: 'availability' },
  subjects: { step: 2, fieldName: 'subjectCards' },
  education: { step: 3, fieldName: 'highestEducationLevel' },
  languages_rate: { step: 4, fieldName: 'teachingLanguages' },
  experience: { step: 5, fieldName: 'tutorExperienceTypes' },
  exam_results: { step: 5, fieldName: 'examResults' },
  teaching_style: { step: 6, fieldName: 'teachingStyle' },
  about_me: { step: 6, fieldName: 'aboutMe' },
  basic_info: { step: 6, fieldName: 'aboutMe' },
};

const mapApiMissingFieldsToItems = (missingFields: TutorCompletionMissingField[]) =>
  missingFields
    .filter((field) => field.severity === 'required')
    .map((field) => {
      const mapping = MISSING_FIELD_KEY_TO_FIELD[field.key];
      if (!mapping) return null;

      return {
        step: mapping.step,
        fieldName: mapping.fieldName,
        anchor: getFieldAnchor(mapping.fieldName),
        label: field.label,
        message: `Please complete ${field.label.toLowerCase()}.`,
      };
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
    .sort((a, b) => a.step - b.step);

const createTermsConsentFingerprint = (data: TutorRegistrationFormData): string => {
  const { agreedToTerms, ...consentRelevantData } = data;
  void agreedToTerms;
  return JSON.stringify(consentRelevantData);
};

const buildCompleteProfileReview = (data: TutorRegistrationFormData) => {
  const stepErrors: Record<number, Record<string, string>> = {};

  stepSchemas.forEach((schema, stepIndex) => {
    const result = schema.safeParse(data);
    if (!result.success) {
      stepErrors[stepIndex] = extractFieldErrors(result.error);
    }
  });

  const isTeacherRelated = hasTeacherRelatedExperience(data.tutorExperienceTypes);
  const hasAnyExamResults = data.examResults.some((exam) =>
    Boolean(exam.examType && exam.results.some((result) => result.subject && result.grade))
  );

  if (!isTeacherRelated && !hasAnyExamResults) {
    stepErrors[5] = {
      ...(stepErrors[5] || {}),
      examResults: 'Please add at least one public exam result so parents can evaluate your academic background.',
    };
  }

  if (!data.aboutMe.trim()) {
    stepErrors[6] = {
      ...(stepErrors[6] || {}),
      aboutMe: 'Please write a short About Me so parents understand your teaching background and style.',
    };
  }

  const orderedSteps = Object.keys(stepErrors)
    .map(Number)
    .sort((a, b) => a - b);

  const missingItems = orderedSteps.flatMap((step) =>
    Object.keys(stepErrors[step]).map((fieldName) => ({
      step,
      fieldName,
      anchor: getFieldAnchor(fieldName),
      label: getFieldLabel(fieldName),
      message: stepErrors[step][fieldName],
    }))
  );

  return {
    stepErrors,
    missingItems,
    firstStep: orderedSteps[0] ?? 0,
    firstAnchor: missingItems[0]?.anchor,
  };
};

const TutorRegistrationModal: React.FC<TutorRegistrationModalProps> = ({
  isOpen,
  onClose,
  options,
}) => {
  const localizePath = useLocalizedPath();
  const { locale } = useOptionalI18n();
  const weeklyTimeTableI18n = getWeeklyTimeTableI18nProps(locale);
  const localizedCurrentStatusOptions = localizeOptions(locale, CURRENT_STATUS_OPTIONS);
  const localizedEducationLevelsHighestOptions = localizeOptions(locale, EDUCATION_LEVELS_HIGHEST);
  const localizedTeachingLanguageOptions = localizeOptions(locale, TEACHING_LANGUAGES);
  const localizedTeachingStyleIAmOptions = localizeStringOptions(locale, TEACHING_STYLE_OPTIONS['I am']);
  const localizedTeachingStyleICanOptions = localizeStringOptions(locale, TEACHING_STYLE_OPTIONS['I can']);
  const localizedTeachingStyleIKnowOptions = localizeStringOptions(locale, TEACHING_STYLE_OPTIONS['I know']);
  const localizedEducationLevelLabels = Object.fromEntries(
    Object.entries(EDUCATION_LEVEL_LABELS).map(([key, value]) => [key, localizeText(locale, value)])
  );
  const { backendUser, updateProfile, profile } = useAuthContext();
  const { openLoginModal, openCloseWarning } = useModal();
  const queryClient = useQueryClient();
  const isCompleteProfileFlow = options?.entryPoint === 'complete-profile';
  const activeRole = profile?.role ?? backendUser?.currentRole ?? null;
  const hasRoleMismatch = Boolean(backendUser && activeRole !== 'tutor');
  const { data: completionStatus } = useTutorCompletionStatus(
    profile?.id,
    isOpen && profile?.role === 'tutor'
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [completeProfileStepErrors, setCompleteProfileStepErrors] = useState<Record<number, Record<string, string>>>({});
  const [activeMissingAnchor, setActiveMissingAnchor] = useState<string | null>(null);
  const [termsConsentFingerprint, setTermsConsentFingerprint] = useState<string | null>(null);
  const [showFeeModal, setShowFeeModal] = useState(false);
  const errorsRef = useRef<Record<string, string>>({});
  const stepScrollRef = useRef<HTMLDivElement>(null);
  const hasInitializedOpenRef = useRef(false);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
const [customSchoolInput, setCustomSchoolInput] = useState('');
  const [showCustomSchoolInput, setShowCustomSchoolInput] = useState(false);
  const [customMajorInput, setCustomMajorInput] = useState('');
  const [showCustomMajorInput, setShowCustomMajorInput] = useState(false);
  const [formData, setFormData] = useState<TutorRegistrationFormData>(createInitialTutorRegistrationFormData);

  // Keep ref in sync with errors state
  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);

  useEffect(() => {
    const el = stepScrollRef.current;
    if (!el) return;
    const check = () => {
      setShowLeftGradient(el.scrollLeft > 0);
      setShowRightGradient(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    return () => el.removeEventListener('scroll', check);
  }, [isOpen]);

  // Real-time error clearing: re-run schema on formData change,
  // clear errors for fields that now pass (never add new errors)
  useEffect(() => {
    const currentErrors = errorsRef.current;
    if (isCompleteProfileFlow) {
      const review = buildCompleteProfileReview(formData);
      setCompleteProfileStepErrors(review.stepErrors);
      const nextErrors = review.stepErrors[currentStep] || {};
      const shouldUpdateErrors =
        JSON.stringify(nextErrors) !== JSON.stringify(currentErrors);

      if (shouldUpdateErrors) {
        setErrors(nextErrors);
      }

      if (activeMissingAnchor && !Object.keys(review.stepErrors).length) {
        setActiveMissingAnchor(null);
      }

      return;
    }

    if (Object.keys(currentErrors).length === 0) return;

    const result = stepSchemas[currentStep].safeParse(formData);

    if (result.success) {
      setErrors({});
      return;
    }

    const stillFailingFields = extractFieldErrors(result.error);
    const updatedErrors: Record<string, string> = {};

    for (const fieldName of Object.keys(currentErrors)) {
      if (stillFailingFields[fieldName]) {
        updatedErrors[fieldName] = stillFailingFields[fieldName];
      }
    }

    if (Object.keys(updatedErrors).length !== Object.keys(currentErrors).length) {
      setErrors(updatedErrors);
    }
  }, [activeMissingAnchor, currentStep, formData, isCompleteProfileFlow]);

  // Reset form to initial state
  const resetForm = () => {
    setCurrentStep(0);
    setFormData(createInitialTutorRegistrationFormData());
    setErrors({});
    setCompleteProfileStepErrors({});
    setActiveMissingAnchor(null);
    setTermsConsentFingerprint(null);
    setCustomSchoolInput('');
    setShowCustomSchoolInput(false);
    setCustomMajorInput('');
    setShowCustomMajorInput(false);
  };

  // Expand multi-level cards to individual subject cards per level
  const expandToSubjectCards = (multiLevelCards: MultiLevelSubjectCard[]): SubjectCard[] => {
    return multiLevelCards
    .flatMap(({ educationSystem, educationLevels, subjects }) =>
      (educationLevels as EducationLevel[]).map((level: EducationLevel) => ({
        educationSystem,
        educationLevel: level,
        subjects: subjects.filter((s) => getSubjectsForLevels([level]).includes(s)),
      }))
    )
    .filter((card) => card.subjects.length > 0);
  };

  // Transform form data to API payload
  const transformFormDataToApiPayload = (data: TutorRegistrationFormData): UpdateTutorProfileRequest => {
    const transformSubjectCards = expandToSubjectCards(data.subjectCards);

    const trimmedAboutMe = data.aboutMe.trim();

    return {
      tuitionType: data.tuitionType as 'physical' | 'online' | 'both',
      preferredState: data.preferredState || undefined,
      preferredDistricts: data.preferredDistricts,
      preferredAreaNotes: data.preferredAreaNotes,
      willingToTravel: deriveWillingToTravel(data.tuitionType),
      maxTravelDistance: data.maxTravelDistance,
      subjectCards: transformSubjectCards,
      currentStatus: data.currentStatus,
      school: data.school,
      teachingInstitution: data.teachingInstitution,
      major: data.major,
      highestEducationLevel: data.highestEducationLevel === 'others' ? data.otherEducationLevel : data.highestEducationLevel,
      availability: data.availability,
      teachingLanguages: data.teachingLanguages,
      customLanguage: data.customLanguage,
      hourlyRate: data.hourlyRate ? Number(data.hourlyRate) : undefined,
      tutorExperienceTypes: data.tutorExperienceTypes,
      yearsOfTuitionExperience: data.yearsOfTuitionExperience ? Number(data.yearsOfTuitionExperience) : undefined,
      examResults: hasTeacherRelatedExperience(data.tutorExperienceTypes) ? [] : data.examResults,
      teachingStyle: data.teachingStyle,
      aboutMe: trimmedAboutMe ? trimmedAboutMe : undefined,
      agreedToTerms: data.agreedToTerms,
    };
  };

  // Validation function for current step
  const validateStep = (step: number): boolean => {
    if (isCompleteProfileFlow) {
      const review = buildCompleteProfileReview(formData);
      setCompleteProfileStepErrors(review.stepErrors);
      const stepFieldErrors = review.stepErrors[step] || {};

      if (Object.keys(stepFieldErrors).length > 0) {
        setErrors(stepFieldErrors);
        setActiveMissingAnchor(getFieldAnchor(Object.keys(stepFieldErrors)[0]));
        return false;
      }

      setErrors({});
      return true;
    }

    const result = stepSchemas[step].safeParse(formData);
    if (!result.success) {
      const fieldErrors = extractFieldErrors(result.error);
      setErrors(fieldErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const nextStep = () => {
    const formDataSnapshot = structuredClone(formData);
    logger.log('TutorRegistration', `Next click snapshot at step ${currentStep + 1}`, formDataSnapshot, true);

    if (validateStep(currentStep)) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
        pushBreadcrumb({ category: 'user', message: `Tutor reg: step ${currentStep + 1} -> ${currentStep + 2}` });
      }
    }
    // if (currentStep < steps.length - 1) {
    //   setCurrentStep(currentStep + 1);
    // }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setErrors({});
      pushBreadcrumb({ category: 'user', message: `Tutor reg: step ${currentStep + 1} -> ${currentStep}` });
    }
  };

  const handleSafeClose = () => {
    const isDirty = formData.loginMethod !== '' || formData.fullName !== '' || formData.tuitionType !== '' || formData.highestEducationLevel !== '' || currentStep > 0;
    if (isDirty && !isSubmitting) {
      openCloseWarning({
        title: localizeText(locale, 'Unsaved Changes'),
        message: localizeText(locale, 'You have unsaved changes in your registration. Are you sure you want to discard them?'),
        cancelText: localizeText(locale, 'Continue Editing'),
        confirmText: localizeText(locale, 'Discard'),
        onConfirm: onClose,
      });
      return;
    }
    setErrors({});
    onClose();
  };

  // Restore pending form data and schedule auto-submit when modal opens
  useEffect(() => {
    if (!isOpen) {
      hasInitializedOpenRef.current = false;
      return;
    }

    if (hasInitializedOpenRef.current) return;

    if (!isOpen) return;

    const initialFormData = createInitialTutorRegistrationFormData();
    const pending = getPendingRegistration();

    // Priority 1: Complete the unauthenticated AI Matching/signup flow.
    // Restore saved form data to the last step so the user can review and submit.
    if (pending?.type === 'tutor') {
      logger.log('TutorRegistration', 'Restoring pending tutor registration');
      const restoredFormData = pending.formData as TutorRegistrationFormData;
      setFormData(restoredFormData);
      setTermsConsentFingerprint(
        restoredFormData.agreedToTerms
          ? createTermsConsentFingerprint(restoredFormData)
          : null
      );
      setCurrentStep(steps.length - 1);
      setActiveMissingAnchor(null);
    } else if (profile && profile.role === 'tutor') {
      // Priority 2: Check if user has existing tutor profile to pre-fill
      // Pre-fill from existing profile
      const prefilledFormData = mapProfileToFormData(profile as ProfileLike, initialFormData);
      setFormData(prefilledFormData);
      setTermsConsentFingerprint(
        prefilledFormData.agreedToTerms
          ? createTermsConsentFingerprint(prefilledFormData)
          : null
      );

      if (isCompleteProfileFlow) {
        const apiMissingItems = mapApiMissingFieldsToItems(
          completionStatus?.missingFields ?? []
        );
        if (apiMissingItems.length > 0) {
          const firstMissingItem = apiMissingItems[0];
          setCurrentStep(firstMissingItem.step);
          setErrors({});
          setActiveMissingAnchor(firstMissingItem.anchor);
        } else {
          const review = buildCompleteProfileReview(prefilledFormData);
          setCompleteProfileStepErrors(review.stepErrors);
          setCurrentStep(review.firstStep);
          setErrors(review.stepErrors[review.firstStep] || {});
          setActiveMissingAnchor(review.firstAnchor || null);
        }
      } else if (!profile.aboutMe) {
        // Default registration workflow keeps the previous convenience jump.
        setCurrentStep(6);
        setActiveMissingAnchor(null);
      }
    }

    hasInitializedOpenRef.current = true;
  }, [completionStatus?.missingFields, isCompleteProfileFlow, isOpen, profile]);

  useEffect(() => {
    if (!isOpen || !isCompleteProfileFlow || !activeMissingAnchor) return;

    const frame = window.requestAnimationFrame(() => {
      const target = document.querySelector<HTMLElement>(`[data-field-anchor="${activeMissingAnchor}"]`);
      if (!target) return;

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const focusable = target.querySelector<HTMLElement>('input, textarea, button, [tabindex]:not([tabindex="-1"])');
      focusable?.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeMissingAnchor, currentStep, isCompleteProfileFlow, isOpen]);

  useEffect(() => {
    if (!isOpen || !isCompleteProfileFlow || !formData.agreedToTerms || !termsConsentFingerprint) {
      return;
    }

    const currentFingerprint = createTermsConsentFingerprint(formData);
    if (currentFingerprint !== termsConsentFingerprint) {
      setFormData((prev) => ({ ...prev, agreedToTerms: false }));
      setTermsConsentFingerprint(null);
    }
  }, [formData, isCompleteProfileFlow, isOpen, termsConsentFingerprint]);

  // Register with DevPanel for debug controls
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!isOpen) {
      window.__devPanel?.unregister('tutor');
      return;
    }
    window.__devPanel?.register({
      type: 'tutor',
      setFormData: (data) => setFormData(data as TutorRegistrationFormData),
      setCurrentStep: setCurrentStep,
      stepCount: steps.length,
      get currentStep() { return currentStep; },
    });
    return () => {
      window.__devPanel?.unregister('tutor');
    };
  }, [isOpen, currentStep]);

  // Register current form state with debug bridge (production-safe)
  useEffect(() => {
    if (!isOpen) {
      window.__debugBridge?.unregisterFormData('tutor');
      return;
    }
    window.__debugBridge?.registerFormData('tutor', () => ({
      formData: formData as unknown as Record<string, unknown>,
      validationErrors: errorsRef.current,
      currentStep,
      stepCount: steps.length,
    }));
    return () => {
      window.__debugBridge?.unregisterFormData('tutor');
    };
  }, [isOpen, currentStep, formData]);

  const handleSubmit = async () => {
    if (hasRoleMismatch) {
      toast.error(localizeText(locale, 'You are currently logged in as a parent. Please switch to a tutor account before submitting your tutor profile.'));
      return;
    }

    if (isCompleteProfileFlow) {
      const review = buildCompleteProfileReview(formData);
      if (review.missingItems.length > 0) {
        setCompleteProfileStepErrors(review.stepErrors);
        setCurrentStep(review.firstStep);
        setErrors(review.stepErrors[review.firstStep] || {});
        setActiveMissingAnchor(review.firstAnchor || null);
        toast.error(localizeText(locale, 'Please complete the highlighted profile sections before submitting.'));
        return;
      }
    }

    if (!formData.agreedToTerms) {
      toast.error(localizeText(locale, 'Please accept the Terms of Service and Privacy Policy to proceed'));
      return;
    }

    // Check if user is authenticated; if not, save form and open login modal
    if (!backendUser) {
      savePendingRegistration('tutor', formData as unknown as Record<string, unknown>);
      onClose();
      openLoginModal({ fromRegistration: true });
      return;
    }

    // Validate Step 6 (last step) before submission
    if (!validateStep(6)) {
      toast.error(localizeText(locale, 'Please fix the errors in Step 7 before submitting'));
      return;
    }

    const result = tutorRegistrationSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors = extractFieldErrors(result.error);
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);
    pushBreadcrumb({ category: 'user', message: 'Tutor reg: submit initiated' });

    try {
      // Transform form data to API payload
      const payload = transformFormDataToApiPayload(formData);

      logger.log('TutorRegistration', 'Submitting profile...');

      // Call API to update tutor profile
      const updatedProfile = await updateTutorProfile(payload);
      clearPendingRegistration();

      logger.log('TutorRegistration', 'API response — verificationStatus: ' + updatedProfile.verificationStatus, updatedProfile);

      // Ensure the profile has role field (API might not include it)
      const profileWithRole = {
        ...updatedProfile,
        role: 'tutor' as const,
      };

      // Update profile in context to trigger NavBar re-render
      updateProfile(profileWithRole);
      logger.log('TutorRegistration', 'updateProfile() called with role=tutor, verificationStatus: ' + profileWithRole.verificationStatus);

      // Invalidate completionStatus cache so NavBar shows updated data
      logger.log('TutorRegistration', 'Invalidating tutorCompletionStatus cache...');
      queryClient.setQueryData(['tutorCompletionStatus', profileWithRole.id], {
        isProfileComplete: true, // since we just successfully registered via the wizard, the profile is 100% complete
        verificationStatus: profileWithRole.verificationStatus,
        completionPercentage: 100,
        hasAboutMe: !!profileWithRole.aboutMe?.trim(),
        canApply: profileWithRole.verificationStatus === 'approved', // depends on validation but set optimistically
        isFullyVerified: Boolean(profileWithRole.isFullyVerified),
        missingFields: [],
      });
      await queryClient.invalidateQueries({ queryKey: ['tutorCompletionStatus'] });
      logger.log('TutorRegistration', 'tutorCompletionStatus cache invalidated and refetched');

      // Show success message
      toast.success(localizeText(locale, 'Tutor profile updated successfully!'));

      // Reset form and close modal
      resetForm();
      onClose();
    } catch (error) {
      logger.error('TutorRegistration', 'Failed to update tutor profile', error);
      captureError('TutorReg:submit', error);
      const errorMessage = error instanceof Error
        ? error.message
        : localizeText(locale, 'Failed to update profile. Please try again.');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Error message component
  const ErrorMessage = ({ fieldName }: { fieldName: string }) => {
    const error = errors[fieldName];
    if (!error) return null;
    return (
      <div className="flex items-start gap-2 text-red-600 text-sm mt-1" role="alert">
        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>{error}</span>
      </div>
    );
  };

  const apiCompleteProfileMissingItems = mapApiMissingFieldsToItems(
    completionStatus?.missingFields ?? []
  );

  const localCompleteProfileMissingItems = Object.keys(completeProfileStepErrors)
    .map(Number)
    .sort((a, b) => a - b)
    .flatMap((step) =>
      Object.keys(completeProfileStepErrors[step]).map((fieldName) => ({
        step,
        fieldName,
        anchor: getFieldAnchor(fieldName),
        label: localizeText(locale, getFieldLabel(fieldName)),
        message: completeProfileStepErrors[step][fieldName],
      }))
    );

  const completeProfileMissingItems = isCompleteProfileFlow
    ? (localCompleteProfileMissingItems.length > 0
        ? localCompleteProfileMissingItems
        : apiCompleteProfileMissingItems)
    : [];

  const hasFieldError = (...fieldNames: string[]) =>
    fieldNames.some((fieldName) => Boolean(errors[fieldName]));

  const getFieldGroupClass = (...fieldNames: string[]) =>
    hasFieldError(...fieldNames)
      ? 'rounded-xl border border-red-300 bg-red-50/40 p-4'
      : '';

  const jumpToMissingField = (step: number, anchor: string) => {
    setCurrentStep(step);
    setErrors(completeProfileStepErrors[step] || {});
    setActiveMissingAnchor(anchor);
  };

  // Helper functions for subject cards
  const addSubjectCard = () => {
    const newCard: MultiLevelSubjectCard = {
      id: Date.now().toString(),
      educationSystem: '',
      educationLevels: [],
      subjects: [],
    };
    setFormData({
      ...formData,
      subjectCards: [...formData.subjectCards, newCard],
    });
  };

  const removeSubjectCard = (id: string) => {
    setFormData({
      ...formData,
      subjectCards: formData.subjectCards.filter((card) => card.id !== id),
    });
  };

  const updateSubjectCard = (id: string, updates: Partial<MultiLevelSubjectCard>) => {
    setFormData({
      ...formData,
      subjectCards: formData.subjectCards.map((card) =>
        card.id === id ? { ...card, ...updates } : card
      ),
    });
  };

  // Helper functions for exam results
  const addExamResult = () => {
    const newExam: ExamResult = {
      id: Date.now().toString(),
      examType: '',
      year: new Date().getFullYear(),
      results: [],
    };
    setFormData({
      ...formData,
      examResults: [...formData.examResults, newExam],
    });
  };

  const removeExamResult = (id: string) => {
    setFormData({
      ...formData,
      examResults: formData.examResults.filter((exam) => exam.id !== id),
    });
  };

  const updateExamResult = (id: string, updates: Partial<ExamResult>) => {
    setFormData({
      ...formData,
      examResults: formData.examResults.map((exam) =>
        exam.id === id ? { ...exam, ...updates } : exam
      ),
    });
  };

  const updateSubjectResult = (
    examId: string,
    subject: string,
    grade: string,
    isCompulsory: boolean
  ) => {
    setFormData({
      ...formData,
      examResults: formData.examResults.map((exam) =>
        exam.id === examId
          ? {
            ...exam,
            results: exam.results.map((result) =>
              result.subject === subject && result.isCompulsory === isCompulsory
                ? { ...result, grade }
                : result
            ),
          }
          : exam
      ),
    });
  };

  const removeSubjectResult = (examId: string, subject: string) => {
    setFormData({
      ...formData,
      examResults: formData.examResults.map((exam) =>
        exam.id === examId
          ? {
            ...exam,
            results: exam.results.filter((result) => result.subject !== subject),
          }
          : exam
      ),
    });
  };

  const isTeacherRelatedExperience = hasTeacherRelatedExperience(formData.tutorExperienceTypes);

  return (
    <>
    <TuitionFeeReferenceModal isOpen={showFeeModal} onClose={() => setShowFeeModal(false)} />
    <Modal
      isOpen={isOpen}
      onClose={handleSafeClose}
      closeOnOverlayClick={false}
      title={localizeText(locale, 'Tutor Registration')}
      maxWidth="max-w-4xl"
      footer={
        <div className="w-full bg-gray-50/50 border-t border-gray-100">
          {currentStep === steps.length - 1 && hasRoleMismatch && (
            <div className="px-1 pb-3 text-sm font-medium text-red-600">
              {localizeText(locale, 'You are currently logged in as a parent. Please switch to a tutor account before submitting your tutor profile.')}
            </div>
          )}
          <div className="flex justify-between items-center gap-4">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`font-medium text-sm transition-colors ${currentStep === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              {localizeText(locale, 'Previous')}
            </button>
            {currentStep < steps.length - 1 ? (
              <button onClick={nextStep} className="btn-primary flex items-center gap-2">
                {localizeText(locale, 'Next')}
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !formData.agreedToTerms || hasRoleMismatch}
                className={`btn-primary flex items-center gap-2 ${(isSubmitting || !formData.agreedToTerms || hasRoleMismatch)
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
                  }`}
              >
                {isSubmitting
                  ? localizeText(locale, 'Submitting...')
                  : localizeText(locale, 'Submit Registration')}
                {!isSubmitting && <ArrowRight className="w-4 h-4" />}
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* Step Progress Indicators */}
      <div className="relative mb-8">
        {showLeftGradient && (
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10" />
        )}
        {showRightGradient && (
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10" />
        )}
        <div ref={stepScrollRef} className="overflow-x-auto scrollbar-hide px-1 py-1 mb-2">
        <div className="flex items-center gap-2 min-w-max">
          {steps.map((_step, index) => (
            <div key={index} className={`flex items-center ${index < steps.length - 1 ? 'flex-1' : ''}`}>
              <div className="flex items-center flex-1">
                {(() => {
                  const stepHasError = Boolean(completeProfileStepErrors[index] && Object.keys(completeProfileStepErrors[index]).length > 0);
                  return (
                <button
                  className={`w-8 h-8 rounded-full font-semibold flex items-center justify-center transition-all flex-shrink-0 ${
                    index < currentStep
                      ? 'bg-primary text-white'
                    : index === currentStep
                      ? 'bg-primary text-white ring-2 ring-primary ring-offset-2'
                    : stepHasError
                      ? 'bg-white border-2 border-red-300 text-red-500'
                      : 'bg-white border-2 border-gray-300 text-gray-400'
                  }`}
                  disabled
                >
                  {index < currentStep ? <Check className="w-4 h-4" /> : index + 1}
                </button>
                  );
                })()}
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 transition-colors ${
                      index < currentStep ? 'bg-primary' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        </div>
        <div className="text-sm font-medium text-gray-600">
          {locale === 'zh-CN'
            ? `第 ${currentStep + 1} 步，共 ${steps.length} 步 — ${localizeText(locale, steps[currentStep])}`
            : `Step ${currentStep + 1} of ${steps.length} — ${steps[currentStep]}`}
        </div>
      </div>

      {isCompleteProfileFlow && completeProfileMissingItems.length > 0 && (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
            <div className="space-y-3">
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  {localizeText(locale, 'Complete the missing sections to finish your tutor profile.')}
                </p>
                <p className="text-sm text-amber-800">
                  {localizeText(locale, 'We brought you to the first missing item and highlighted what still needs attention.')}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {completeProfileMissingItems.map((item) => (
                  <button
                    key={`${item.step}-${item.fieldName}`}
                    type="button"
                    onClick={() => jumpToMissingField(item.step, item.anchor)}
                    className="rounded-full border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-900 transition-colors hover:bg-amber-100"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step Content */}
      <div className="min-h-[400px]">
        {/* Step 0: Tuition Type */}
        {currentStep === 0 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-5 bg-accent rounded-full" />
              <h3 className="text-lg font-semibold text-gray-900">{localizeText(locale, 'Type of Tuition')}</h3>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
              <p className="text-sm text-gray-600">
                {localizeText(locale, 'Select all the types of tuition you can provide')}
              </p>
              {errors.tuitionType && <ErrorMessage fieldName="tuitionType" />}
              <div
                data-field-anchor="tuitionType"
                className={`grid grid-cols-1 gap-4 md:grid-cols-3 ${getFieldGroupClass('tuitionType')}`}
              >
                <div className="relative min-w-0">
                  <button
                    type="button"
                    onClick={() => {
                      const newTypes = formData.tuitionType === 'physical'
                        ? ''
                        : formData.tuitionType === 'both'
                          ? 'online'
                          : 'physical';
                      setFormData({ ...formData, tuitionType: newTypes });
                    }}
                    className={`flex min-h-[11rem] w-full flex-col items-center justify-center gap-3 rounded-xl px-4 py-5 font-medium transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${formData.tuitionType === 'physical' || formData.tuitionType === 'both'
                      ? 'bg-primary-50 border-2 border-primary text-primary shadow-sm'
                      : 'bg-white border border-gray-200 text-gray-600 hover:border-primary-200 hover:bg-primary-50/50'
                      }`}
                  >
                    <TuitionTypeOptionContent
                      type="physical"
                      label={localizeText(locale, 'Physical Tuition')}
                      showSelectedIndicator={formData.tuitionType === 'physical' || formData.tuitionType === 'both'}
                      selectedLabel={localizeText(locale, 'Selected')}
                    />
                  </button>
                  <span role="note" className="pointer-events-none absolute right-2 top-0 z-10 -translate-y-1/2 whitespace-nowrap rounded-lg border border-primary-200 bg-primary-50 px-2 py-1 text-center text-[11px] font-semibold leading-4 text-primary shadow-sm">
                    {localizeText(locale, '98.3% of parents choose this')}
                  </span>
                </div>

                <button
                  onClick={() => {
                    const newTypes = formData.tuitionType === 'online'
                      ? ''
                      : formData.tuitionType === 'both'
                        ? 'physical'
                        : 'online';
                    setFormData({ ...formData, tuitionType: newTypes });
                  }}
                  className={`flex min-h-[11rem] flex-col items-center justify-center gap-3 rounded-xl px-4 py-5 font-medium transition-all cursor-pointer ${formData.tuitionType === 'online' || formData.tuitionType === 'both'
                    ? 'bg-primary-50 border-2 border-primary text-primary shadow-sm'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-primary-200 hover:bg-primary-50/50'
                    }`}
                >
                  <TuitionTypeOptionContent
                    type="online"
                    label={localizeText(locale, 'Online Tuition')}
                    showSelectedIndicator={formData.tuitionType === 'online' || formData.tuitionType === 'both'}
                    selectedLabel={localizeText(locale, 'Selected')}
                  />
                </button>

                <button
                  onClick={() => {
                    const newTypes = formData.tuitionType === 'both'
                      ? ''
                      : 'both';
                    setFormData({ ...formData, tuitionType: newTypes });
                  }}
                  className={`flex min-h-[11rem] flex-col items-center justify-center gap-3 rounded-xl px-4 py-5 font-medium transition-all cursor-pointer ${formData.tuitionType === 'both'
                    ? 'bg-primary-50 border-2 border-primary text-primary shadow-sm'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-primary-200 hover:bg-primary-50/50'
                    }`}
                >
                  <TuitionTypeOptionContent
                    type="both"
                    label={localizeText(locale, 'Both')}
                    showSelectedIndicator={formData.tuitionType === 'both'}
                    selectedLabel={localizeText(locale, 'Selected')}
                  />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Teaching Preferences (Area + Time) */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-5 bg-accent rounded-full" />
              <h3 className="text-lg font-semibold text-gray-900">{localizeText(locale, 'Teaching Preferences')}</h3>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
              {errors.preferredState && <ErrorMessage fieldName="preferredState" />}
              {errors.preferredDistricts && <ErrorMessage fieldName="preferredDistricts" />}
              {errors.availability && <ErrorMessage fieldName="availability" />}

              {/* Cascading State → Districts → Area Notes (hidden for online) */}
              <div className="space-y-4">
                {formData.tuitionType !== 'online' && (
                  <div
                    data-field-anchor="preferredState"
                    className={getFieldGroupClass('preferredState')}
                  >
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {localizeText(locale, 'State')} <span className="text-red-500">*</span>
                    </label>
                    <SearchableDropdown
                      options={STATE_OPTIONS}
                      value={formData.preferredState}
                      onChange={(value) => {
                        setFormData({
                          ...formData,
                          preferredState: String(value),
                          preferredDistricts: [],
                          preferredAreaNotes: [],
                        });
                      }}
                      placeholder={localizeText(locale, 'Select a state...')}
                      searchable={true}
                    />
                  </div>
                )}

                {formData.preferredState && formData.tuitionType !== 'online' && (
                  <div
                    data-field-anchor="preferredDistricts"
                    className={getFieldGroupClass('preferredDistricts')}
                  >
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {localizeText(locale, 'Preferred Districts (Select multiple)')} <span className="text-red-500">*</span>
                    </label>
                    <MultiSelect
                      options={STATE_DISTRICTS[formData.preferredState as keyof typeof STATE_DISTRICTS] || []}
                      value={formData.preferredDistricts}
                      onChange={(value) => setFormData({ ...formData, preferredDistricts: value })}
                      placeholder={localizeText(locale, 'Select districts...')}
                      displayMode="tags"
                      searchable={true}
                      showSelected={true}
                    />
                  </div>
                )}

                {formData.preferredState && formData.tuitionType !== 'online' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {localizeText(locale, 'Specific Areas / Neighbourhoods')} <span className="text-gray-400 font-normal">({localizeText(locale, 'Optional')})</span>
                    </label>
                    <AreaNoteInput
                      onAdd={(note) => {
                        if (note && !formData.preferredAreaNotes.includes(note)) {
                          setFormData({ ...formData, preferredAreaNotes: [...formData.preferredAreaNotes, note] });
                        }
                      }}
                    />
                    {formData.preferredAreaNotes.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.preferredAreaNotes.map((note) => (
                          <span
                            key={note}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-accent/10 text-accent-700 text-sm rounded-md border border-accent/20"
                          >
                            {note}
                            <button
                              type="button"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  preferredAreaNotes: formData.preferredAreaNotes.filter((x) => x !== note),
                                })
                              }
                              className="hover:text-accent-800"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Weekly Time Table */}
              <div className="border-t pt-4">
                <div
                  data-field-anchor="availability"
                  className={getFieldGroupClass('availability')}
                >
                  <WeeklyTimeTable
                    selectedTimes={formData.availability}
                    onChange={(availability) => setFormData({ ...formData, availability })}
                    customTimeRanges={formData.availabilityTimeRanges}
                    onCustomTimeRangesChange={(availabilityTimeRanges) => setFormData({ ...formData, availabilityTimeRanges })}
                    label={localizeText(locale, 'Preferred Teaching Times')}
                    helperText={localizeText(locale, "Click on the grid to select when you're available to teach")}
                    {...weeklyTimeTableI18n}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Subjects by Education Level - Card Design */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-1 h-5 bg-accent rounded-full" />
                <h3 className="text-lg font-semibold text-gray-900">{localizeText(locale, 'Subjects You Can Teach')}</h3>
              </div>
              <button
                type="button"
                onClick={addSubjectCard}
                className="flex items-center gap-2 px-3 py-2 bg-accent text-white rounded-lg hover:bg-accent-600 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
            {errors.subjectCards && <ErrorMessage fieldName="subjectCards" />}

            <div data-field-anchor="subjectCards" className={getFieldGroupClass('subjectCards')}>
            <div className="space-y-4">
              {(() => {
                logger.log('TutorRegistration', 'Subject cards length: ' + formData.subjectCards.length);
                return formData.subjectCards.map((card) => (
                  <Card
                    key={card.id}
                    header={
                      <label className="text-sm font-medium text-gray-700">
                        {localizeText(locale, 'Education Level(s)')} <span className="text-red-500">*</span>
                      </label>
                    }
                    headerAction={
                      formData.subjectCards.length > 1 ? (
                        <button
                          type="button"
                          onClick={() => removeSubjectCard(card.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title={localizeText(locale, 'Remove')}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      ) : undefined
                    }
                  >
                  <div className="space-y-4">
                    <div>
                      {/* <label className="block text-sm font-medium text-gray-700 mb-2">
                        Education System <span className="text-red-500">*</span>
                      </label> */}
                      <SearchableDropdown
                        options={EDUCATION_SYSTEM_OPTIONS}
                        value={card.educationSystem}
                        onChange={(value) => {
                          updateSubjectCard(card.id, {
                            educationSystem: String(value),
                            educationLevels: [],
                            subjects: [],
                          });
                        }}
                        placeholder={localizeText(locale, 'Select education system')}
                        searchable={true}
                      />
                    </div>
                    <div>
                      <LevelMultiSelect
                        value={card.educationLevels as EducationLevel[]}
                        onChange={(levels) => {
                          updateSubjectCard(card.id, {
                            educationLevels: levels,
                            subjects: [],
                          });
                        }}
                        placeholder={localizeText(locale, 'Select education level(s)')}
                        disabled={!card.educationSystem}
                        systemId={card.educationSystem}
                        optionLabels={localizedEducationLevelLabels}
                      />
                    </div>

                    {card.educationLevels.length > 0 && (
                      <div>
                        <MultiSelect
                          options={getSubjectsForLevels(card.educationLevels as EducationLevel[])}
                          value={card.subjects}
                          onChange={(value) => updateSubjectCard(card.id, { subjects: value })}
                          placeholder={localizeText(locale, 'Select subjects')}
                          displayMode="tags"
                          maxDisplayTags={2}
                          searchable={true}
                        />
                      </div>
                    )}
                  </div>
                </Card>
                ));
              })()}
            </div>
            </div>
          </div>
        )}

        {/* Step 3: Personal Profile (Education + Teaching Languages) */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-5 bg-accent rounded-full" />
              <h3 className="text-lg font-semibold text-gray-900">{localizeText(locale, 'Personal Profile')}</h3>
            </div>

            {/* Current Status - Tab Style */}
            <div data-field-anchor="currentStatus" className={getFieldGroupClass('currentStatus')}>
              <TabButtonGroup
                options={localizedCurrentStatusOptions}
                value={formData.currentStatus}
                onChange={(value) => setFormData({ ...formData, currentStatus: value as string })}
                label={localizeText(locale, 'Current Status')}
                isRequired={true}
              />
              {errors.currentStatus && <ErrorMessage fieldName="currentStatus" />}
            </div>

            {/* Highest Education Level - Tab Style */}
            <div data-field-anchor="highestEducationLevel" className={getFieldGroupClass('highestEducationLevel', 'otherEducationLevel')}>
              <TabButtonGroup
                options={localizedEducationLevelsHighestOptions}
                value={formData.highestEducationLevel}
                onChange={(value) => setFormData({ ...formData, highestEducationLevel: value as string })}
                label={localizeText(locale, 'Highest Education Level')}
                isRequired={true}
              />
              {errors.highestEducationLevel && <ErrorMessage fieldName="highestEducationLevel" />}

              {/* Custom Education Level Input */}
              {formData.highestEducationLevel === 'others' && (
                <div className="mt-4">
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder={localizeText(locale, 'Enter your education level')}
                    value={formData.otherEducationLevel}
                    onChange={(e) => setFormData({ ...formData, otherEducationLevel: e.target.value })}
                  />
                  {errors.otherEducationLevel && <ErrorMessage fieldName="otherEducationLevel" />}
                </div>
              )}
            </div>

            {/* School with SearchableDropdown + UNIVERSITIES */}
            <div data-field-anchor="school" className={getFieldGroupClass('school')}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {formData.currentStatus === 'studying'
                  ? 'Current Studying University / School'
                  : localizeText(locale, 'School / University')} <span className="text-red-500">*</span>
              </label>
              <SearchableDropdown
                options={UNIVERSITIES}
                value={formData.school}
                onChange={(value) => setFormData({ ...formData, school: String(value) })}
                placeholder={localizeText(locale, 'Search for a school or university...')}
                searchable={true}
                allowCustom={true}
              />
              {errors.school && <ErrorMessage fieldName="school" />}

              {/* Custom School Input */}
              <div className="mt-4">
                {showCustomSchoolInput ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder={localizeText(locale, 'Enter custom school or university name...')}
                      value={customSchoolInput}
                      onChange={(e) => setCustomSchoolInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (customSchoolInput.trim()) {
                            setFormData({
                              ...formData,
                              school: customSchoolInput.trim(),
                            });
                            setCustomSchoolInput('');
                            setShowCustomSchoolInput(false);
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (customSchoolInput.trim()) {
                          setFormData({
                            ...formData,
                            school: customSchoolInput.trim(),
                          });
                          setCustomSchoolInput('');
                          setShowCustomSchoolInput(false);
                        }
                      }}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
                    >
                      {localizeText(locale, 'Add')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCustomSchoolInput('');
                        setShowCustomSchoolInput(false);
                      }}
                      className="px-3 py-2 text-gray-500 hover:text-gray-700 transition-colors text-sm"
                    >
                      {localizeText(locale, 'Cancel')}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowCustomSchoolInput(true)}
                    className="flex items-center gap-2 text-primary hover:text-primary-600 font-medium text-sm transition-colors mt-2"
                  >
                    <Plus className="w-4 h-4" />
                    {localizeText(locale, 'Add Custom School/University')}
                  </button>
                )}
              </div>
            </div>

            {/* Major of Study */}
            <div data-field-anchor="major" className={getFieldGroupClass('major')}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {localizeText(locale, 'Major of Study')} <span className="text-red-500">*</span>
              </label>
              <SearchableInput
                options={MAJOR_OPTIONS}
                value={formData.major}
                onChange={(value) => setFormData({ ...formData, major: value })}
                placeholder={localizeText(locale, 'Search for a field of study or enter manually...')}
              />
              {errors.major && <ErrorMessage fieldName="major" />}

              {/* Custom Major Input */}
              <div className="mt-4">
                {showCustomMajorInput ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder={localizeText(locale, 'Enter custom major of study...')}
                      value={customMajorInput}
                      onChange={(e) => setCustomMajorInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (customMajorInput.trim()) {
                            setFormData({
                              ...formData,
                              major: customMajorInput.trim(),
                            });
                            setCustomMajorInput('');
                            setShowCustomMajorInput(false);
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (customMajorInput.trim()) {
                          setFormData({
                            ...formData,
                            major: customMajorInput.trim(),
                          });
                          setCustomMajorInput('');
                          setShowCustomMajorInput(false);
                        }
                      }}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
                    >
                      {localizeText(locale, 'Add')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCustomMajorInput('');
                        setShowCustomMajorInput(false);
                      }}
                      className="px-3 py-2 text-gray-500 hover:text-gray-700 transition-colors text-sm"
                    >
                      {localizeText(locale, 'Cancel')}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowCustomMajorInput(true)}
                    className="flex items-center gap-2 text-primary hover:text-primary-600 font-medium text-sm transition-colors mt-2"
                  >
                    <Plus className="w-4 h-4" />
                    {localizeText(locale, 'Add Custom Major')}
                  </button>
                )}
              </div>
            </div>

            {/* Teaching Languages (merged from old Step 5) */}
            <div data-field-anchor="teachingLanguages" className={`border-t pt-6 ${getFieldGroupClass('teachingLanguages', 'customLanguage')}`}>
              <TabButtonGroup
                options={localizedTeachingLanguageOptions}
                value={formData.teachingLanguages}
                onChange={(value) => {
                  const languages = value as string[];
                  setFormData({ ...formData, teachingLanguages: languages });
                }}
                label={localizeText(locale, 'Languages You Can Teach In')}
                multiple={true}
                isRequired={true}
              />
              {errors.teachingLanguages && <ErrorMessage fieldName="teachingLanguages" />}

              {/* Custom Language Input - shown when "Other" is selected */}
              {formData.teachingLanguages.includes('other') && (
                <div className="mt-4">
                  <input
                    type="text"
                    placeholder={localizeText(locale, 'e.g., French, German, Japanese, Korean...')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    value={formData.customLanguage}
                    onChange={(e) => setFormData({ ...formData, customLanguage: e.target.value })}
                  />
                  {errors.customLanguage && <ErrorMessage fieldName="customLanguage" />}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Hourly Rate */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-5 bg-primary rounded-full" />
              <h3 className="text-lg font-semibold text-gray-900">{localizeText(locale, 'Hourly Teaching Rate')}</h3>
            </div>
            <div data-field-anchor="hourlyRate" className={getFieldGroupClass('hourlyRate')}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  {localizeText(locale, 'Set your hourly rate')} <span className="text-red-500">*</span>
                </span>
                <button
                  type="button"
                  onClick={() => setShowFeeModal(true)}
                  className="text-xs font-medium text-secondary hover:text-primary transition-colors underline underline-offset-2"
                >
                  {localizeText(locale, 'View Rate Reference')}
                </button>
              </div>
              <SingleRangeSlider
                min={10}
                max={800}
                value={formData.hourlyRate ? Number(formData.hourlyRate) : 50}
                onChange={(value) => setFormData({ ...formData, hourlyRate: value.toString() })}
                unit="RM"
              />
              {errors.hourlyRate && <ErrorMessage fieldName="hourlyRate" />}
            </div>
          </div>
        )}

        {/* Step 5: Tutor Experience + Public Exam Results */}
        {currentStep === 5 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-5 bg-accent rounded-full" />
              <h3 className="text-lg font-semibold text-gray-900">{localizeText(locale, 'Tutor Experience')}</h3>
            </div>
            <div data-field-anchor="tutorExperienceTypes" className={getFieldGroupClass('tutorExperienceTypes')}>
              <h4 className="text-base font-semibold mb-3">{localizeText(locale, 'Experience Types')}</h4>
              <p className="text-sm text-gray-600 mb-4">{localizeText(locale, 'Select all that apply:')}</p>
              {errors.tutorExperienceTypes && <ErrorMessage fieldName="tutorExperienceTypes" />}
              <div className="flex flex-wrap gap-2 mt-4">
                {TUTOR_EXPERIENCE_TYPES.map((type) => {
                  const isNoExpSelected = formData.tutorExperienceTypes.includes('No experience');
                  const isDisabled = isNoExpSelected && type !== 'No experience';
                  return (
                    <button
                      key={type}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => {
                        const isAdding = !formData.tutorExperienceTypes.includes(type);

                        if (type === 'No experience') {
                          setFormData({
                            ...formData,
                            tutorExperienceTypes: isAdding ? ['No experience'] : [],
                            yearsOfTuitionExperience: isAdding ? '' : formData.yearsOfTuitionExperience,
                            examResults: [],
                          });
                          return;
                        }

                        let newTypes = isAdding
                          ? [...formData.tutorExperienceTypes.filter((t) => t !== 'No experience'), type]
                          : formData.tutorExperienceTypes.filter((t) => t !== type);

                        // Mutual exclusivity: Current vs Retired School Teacher
                        if (type === 'Current School Teacher' && isAdding) {
                          newTypes = newTypes.filter((t) => t !== 'Retired School Teacher');
                        } else if (type === 'Retired School Teacher' && isAdding) {
                          newTypes = newTypes.filter((t) => t !== 'Current School Teacher');
                        }

                        // Mutual exclusivity: Part-time vs Full-time Tuition Center Tutor
                        if (type === 'Part-time Tuition Center Tutor' && isAdding) {
                          newTypes = newTypes.filter((t) => t !== 'Full-time Tuition Center Tutor');
                        } else if (type === 'Full-time Tuition Center Tutor' && isAdding) {
                          newTypes = newTypes.filter((t) => t !== 'Part-time Tuition Center Tutor');
                        }

                        setFormData({
                          ...formData,
                          tutorExperienceTypes: newTypes,
                          examResults: hasTeacherRelatedExperience(newTypes) ? [] : formData.examResults,
                        });
                      }}
                      className={`px-4 py-2 rounded-full font-medium text-sm transition-colors ${
                        formData.tutorExperienceTypes.includes(type)
                          ? 'bg-accent text-white'
                          : isDisabled
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {localizeText(locale, type)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div data-field-anchor="yearsOfTuitionExperience" className={getFieldGroupClass('yearsOfTuitionExperience')}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {localizeText(locale, 'Years of Experience')} <span className="text-red-500">*</span>
              </label>
              <SearchableDropdown
                options={TUITION_EXPERIENCE_YEARS.map(opt => ({ value: String(opt.value), label: opt.label }))}
                value={String(formData.yearsOfTuitionExperience)}
                onChange={(value) => {
                  setFormData({ ...formData, yearsOfTuitionExperience: String(value) });
                }}
                placeholder={localizeText(locale, 'Select years of experience')}
                searchable={false}
                disabled={formData.tutorExperienceTypes.includes('No experience')}
              />
              {errors.yearsOfTuitionExperience && <ErrorMessage fieldName="yearsOfTuitionExperience" />}
            </div>

            {isTeacherRelatedExperience && (
              <div className="border-t pt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {localizeText(locale, 'School / Teaching Institution')} <span className="text-gray-400">({localizeText(locale, 'optional')})</span>
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  {localizeText(locale, 'Sharing the school or institution where you teach can significantly improve your expected hourly rate and selection chances.')}
                </p>
                <input
                  type="text"
                  value={formData.teachingInstitution}
                  onChange={(e) => setFormData({ ...formData, teachingInstitution: e.target.value })}
                  placeholder={localizeText(locale, 'e.g. SMK Taman Desa, ABC Kindergarten')}
                  className="input-field"
                />
              </div>
            )}

            {/* Public Exam Results (merged into this step) */}
            {!isTeacherRelatedExperience && (
            <div data-field-anchor="examResults" className={`border-t pt-6 ${getFieldGroupClass('examResults')}`}>
              <PastPublicExamResults
                examResults={formData.examResults}
                onAddExamResult={addExamResult}
                onRemoveExamResult={removeExamResult}
                onUpdateExamResult={updateExamResult}
                onUpdateSubjectResult={updateSubjectResult}
                onRemoveSubjectResult={removeSubjectResult}
              />
            </div>
            )}
          </div>
        )}

        {/* Step 6: Teaching Style + About Me + Terms */}
        {currentStep === 6 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-5 bg-accent rounded-full" />
              <h3 className="text-lg font-semibold text-gray-900">{localizeText(locale, 'Your Teaching Style')}</h3>
            </div>

            {/* I am */}
            <TabButtonGroup
              options={localizedTeachingStyleIAmOptions}
              value={formData.teachingStyle.iAm}
              onChange={(value) => {
                setFormData({
                  ...formData,
                  teachingStyle: { ...formData.teachingStyle, iAm: value as string[] },
                });
              }}
              label={localizeText(locale, 'I am')}
              multiple={true}
            />

            {/* I can */}
            <TabButtonGroup
              options={localizedTeachingStyleICanOptions}
              value={formData.teachingStyle.iCan}
              onChange={(value) => {
                setFormData({
                  ...formData,
                  teachingStyle: { ...formData.teachingStyle, iCan: value as string[] },
                });
              }}
              label={localizeText(locale, 'I can')}
              multiple={true}
            />

            {/* I know */}
            <TabButtonGroup
              options={localizedTeachingStyleIKnowOptions}
              value={formData.teachingStyle.iKnow}
              onChange={(value) => {
                setFormData({
                  ...formData,
                  teachingStyle: { ...formData.teachingStyle, iKnow: value as string[] },
                });
              }}
              label={localizeText(locale, 'I know')}
              multiple={true}
            />

            {/* Others */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {localizeText(locale, 'Others (Optional)')}
              </label>
              <input
                type="text"
                placeholder={localizeText(locale, 'Add any other teaching characteristics...')}
                className="input-field"
                value={formData.teachingStyle.others}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    teachingStyle: { ...formData.teachingStyle, others: e.target.value },
                  })
                }
              />
            </div>

            {/* About Me */}
            <div data-field-anchor="aboutMe" className={`border-t pt-6 ${getFieldGroupClass('aboutMe')}`}>
              <h3 className="text-xl font-semibold mb-4">{localizeText(locale, 'About Me')}</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {localizeText(locale, 'Tell us about yourself')} <span className="text-gray-500">({localizeText(locale, 'Optional')})</span>
                </label>
                <p className="text-xs text-gray-600 mb-3">
                  {localizeText(locale, "Tutors who complete this section are more likely to be selected by parents. If you don't have time now, you can fill this in later.")}
                </p>
                <div className="space-y-3">
                  <textarea
                    placeholder={localizeText(locale, 'Say something you want others to know more about you!')}
                    className={`input-field resize-none ${errors.aboutMe ? 'border-red-500' : ''}`}
                    rows={6}
                    value={formData.aboutMe}
                    onChange={(e) => setFormData({ ...formData, aboutMe: e.target.value })}
                  />
                  {errors.aboutMe && <ErrorMessage fieldName="aboutMe" />}
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-gray-500">
                      {formData.aboutMe
                        ? (locale === 'zh-CN'
                          ? `字数：${formData.aboutMe.split(/\s+/).filter((word) => word.length > 0).length}`
                          : `Word count: ${formData.aboutMe.split(/\s+/).filter((word) => word.length > 0).length}`)
                        : localizeText(locale, 'Minimum 20 words if filled')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Terms & Conditions */}
            <div data-field-anchor="agreedToTerms" className={`border-t pt-6 ${getFieldGroupClass('agreedToTerms')}`}>
              <h3 className="text-xl font-semibold mb-4">{localizeText(locale, 'Terms & Conditions')}</h3>
              <div className="flex items-start space-x-3 p-4 bg-accent-50 border border-accent-200 rounded-lg">
                <input
                  type="checkbox"
                  id="terms"
                  checked={formData.agreedToTerms}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const nextFormData = { ...formData, agreedToTerms: checked };
                    setFormData(nextFormData);
                    setTermsConsentFingerprint(
                      checked ? createTermsConsentFingerprint(nextFormData) : null
                    );
                  }}
                  className="w-5 h-5 mt-0.5 cursor-pointer"
                />
                <label htmlFor="terms" className="text-sm text-gray-700 cursor-pointer">
                  {localizeText(locale, 'I agree to the')}{' '}
                  <a
                    href={localizePath('/terms-tutors')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent underline hover:text-accent-600"
                  >
                    {localizeText(locale, 'Terms of Service')}
                  </a>{' '}
                  {localizeText(locale, 'and')}{' '}
                  <a
                    href={localizePath('/privacy-policy')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent underline hover:text-accent-600"
                  >
                    {localizeText(locale, 'Privacy Policy')}
                  </a>
                  {localizeText(locale, '. I confirm that all information provided is accurate and truthful.')} <span className="text-red-500">*</span>
                </label>
              </div>
              {errors.agreedToTerms && <ErrorMessage fieldName="agreedToTerms" />}
            </div>
          </div>
        )}
      </div>
    </Modal>
    </>
  );
};

export default TutorRegistrationModal;
